import React, {useMemo} from "react";
import { PersonAndBoxMessage } from "../../../components/messages/PersonAndBoxMessage";
import { useSnackbar } from "../../../context/snackbar-context";
import { useSnackbarDisplayOnError } from "../../../hooks/error/useSnackbarDisplayOnError";
import { useBindSearchQuery } from "../../../hooks/useBindSearchQuery";
import { useGetBoundReportPaginated } from "../../../hooks/request/useGetBoundReportPaginated";
import { BoundTablePagination } from "../../../components/common/inputs/boundInputs";
import { BuyerInterchangeSortFilter, BuyerInterchangeSearchParams } from "../../../components/buyer/interchangesReport/BuyerInterchangeSortFilter";
import { BuyerInterchangeCardListWithLoader } from "../../../components/buyer/interchangesReport/BuyerInterchangeCardList";
import { buyerInterchangesReportEndpoints as reportEndpoints } from "../../../requests/api-endpoints";
import { useReportDataSummaryCollapseByIndex}  from "../../../hooks/report/useReportDataSummaryCollapseByIndex";
import { useBoundQueryDownloadUrl } from "../../../hooks/report/useBoundQueryDownloadUrl";
import DownloadCSVButton from "../../../components/report/export/DownloadCSVButton";
import {
  DownloadButtonsContainer,
  ReportDataSummary
} from "../../../components/report/redesigned";
import {
  SecurSpaceReportTitle
} from "../../../components/common";
import { BuyerInventoryActivityReportVO } from "../../../types/model/valueObject/inventoryActivity/BuyerInventoryActivityReportVO";
import { BoundSearchQuery } from "../../../types/common/BoundSearchQuery";
import { DateFormats, useGetRelativeDate } from "../../../hooks/report/useGetRelativeDate";
import { fromBuyerInventoryActivitySearchCriteria } from "../../../types/model/criteria/inventoryActivity/BuyerInventoryAuditSearchCriteria";
import { BUYER_INTERCHANGES_DATE_BYPASS_KEYS } from "../../../types/model/criteria/inventoryActivity/BuyerInventoryActivitySearchCriteria";
import { withDateRangeBypass } from "../../../util/ReportDateRangeUtils";

// a default query MUST be defined outside of any components to avoid re-initialization on each render
const DEFAULT_QUERY: BuyerInterchangeSearchParams = {
  //startDate: RELATIVE DATE: define it within the component using `useGetRelativeDate`
  //endDate: RELATIVE DATE: define it within the component using `useGetRelativeDate`
  locationId: '',
  supplierId: '',
  bookingNumber: '',
  equipmentNumber: '',
  equipmentType: '',
  sortBy: "ACTIVITY_DATE",
  sortDir: 'desc',
  page: 0,
  size: 10,
};

const QUERY_PARAM_RENAMES: Record<string, string> = {
  bookingNumber: 'orderNumber',
};

const BuyerInterchangesReport = () => {
  const { snackbarShowMessage } = useSnackbar();
  // state hooks
const defaultStartDate = useGetRelativeDate(-1, 'month', DateFormats.DAY);
  const defaultEndDate = useGetRelativeDate(0, 'day', DateFormats.DAY);
  const defaultQuery = useMemo(() => ({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    ...DEFAULT_QUERY
  }), [defaultStartDate, defaultEndDate]);
  const [reportQuery, setReportQuery] = useBindSearchQuery(defaultQuery, {
    paramRenames: QUERY_PARAM_RENAMES
  });
  const requestQuery = useMemo(() => withDateRangeBypass(reportQuery, BUYER_INTERCHANGES_DATE_BYPASS_KEYS), [reportQuery]);

  // Api hooks
  const {
    reportPayload,
    pageInfo,
    isLoading,
    error
  } =  useGetBoundReportPaginated<BuyerInventoryActivityReportVO[]>({
    requestUrl: reportEndpoints.search,
    requestParams: requestQuery
  });

  const {
    collapseByIndex: expandByIndex,
    handleToggleCollapse: handleToggleExpand,
    hasExpandedItems,
    setExpandedItems,
    setExpandedAll,
    handleExpandAll,
  } = useReportDataSummaryCollapseByIndex({ data: reportPayload });

  // error hooks
  useSnackbarDisplayOnError({error, snackbarShowMessage });

  // conditional display hooks
  // Not gated on `isLoading`: the previous payload survives a refetch, so
  // leaving this on keeps the empty state put instead of blinking out and
  // back on every filter change. It is undefined until the first load, so it
  // cannot flash before there is an answer.
  const shouldShowPersonAndBox = useMemo(() => {
    return !error && reportPayload?.length === 0
  }, [error, reportPayload]);

  const showPagination = useMemo(() => {
    return !!(reportPayload?.length && reportPayload.length > 0);
  }, [reportPayload])

  // change handlers
  const handleFilterChange = (newQuery: BuyerInterchangeSearchParams) => {
    const updatedQuery = {
      ...reportQuery,
      ...newQuery
    };
    if (!newQuery.page) {
      // reset to first page on filter change if it is not being explicitly set
      updatedQuery.page = 0;
    }

    setReportQuery(updatedQuery);
  }

  const downloadUrl = useBoundQueryDownloadUrl({ endpoint: reportEndpoints.report, query: requestQuery });
  // Spreading yields an anonymous object type, which (unlike an interface) is assignable to BoundSearchQuery's index signature.
  const downloadSummaryQuery: BoundSearchQuery = { ...fromBuyerInventoryActivitySearchCriteria(requestQuery as BuyerInterchangeSearchParams) };
  const downloadSummaryUrl = useBoundQueryDownloadUrl({ endpoint: reportEndpoints.summaryReport, query: downloadSummaryQuery });

  //NOTE: since this report is contained within the ReportOverview tabs, it should not be wrapped in a
  //      SecurSpaceContainer or SecurSpaceReportContainer, as those are already present in the parent
  //      component
  return (
    <>
      <SecurSpaceReportTitle>Interchanges</SecurSpaceReportTitle>
      <BuyerInterchangeSortFilter
        query={reportQuery}
        onChange={handleFilterChange}
      />
      <ReportDataSummary
        visible={!shouldShowPersonAndBox && !isLoading}
        count={pageInfo?.count || 0}
        title={"All"}
        isShowCollapseButton={true}
        hasExpandedItems={hasExpandedItems}
        setExpandedItems={setExpandedItems}
        setExpandedAll={setExpandedAll}
        handleExpandAll={handleExpandAll}
        downloadButtonsSlot={
          <DownloadButtonsContainer>
            <DownloadCSVButton title='Download Summary' reportUrl={downloadSummaryUrl} />
            <DownloadCSVButton title='Download Report' reportUrl={downloadUrl} />
          </DownloadButtonsContainer>
        }
      />
      <BuyerInterchangeCardListWithLoader
        visible={!error}
        isLoading={isLoading}
        items={reportPayload}
        expandByIndex={expandByIndex}
        onToggleExpand={handleToggleExpand}
      />
      <PersonAndBoxMessage
        visible={shouldShowPersonAndBox }
      />
      <BoundTablePagination<BuyerInterchangeSearchParams>
        visible={showPagination}
        pageInfo={pageInfo}
        onChange={handleFilterChange}
      />
    </>
  );
};

export default BuyerInterchangesReport;
